import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../services/firebase";
import WineCard from "../components/WineCard";

/* ===== Utils texte ===== */
function norm(str = "") {
  return String(str)
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();
}
const tokenize = (s) => norm(s).split(" ").filter(Boolean);

/* Distance d’édition bornée (rapide) */
function levenshteinMax2(a, b) {
  // Arrêt rapide si trop différent
  if (Math.abs(a.length - b.length) > 2) return 3;
  const m = a.length, n = b.length;
  const dp = new Array(n + 1);
  for (let j = 0; j <= n; j++) dp[j] = j;
  for (let i = 1; i <= m; i++) {
    let prev = dp[0];
    dp[0] = i;
    for (let j = 1; j <= n; j++) {
      const tmp = dp[j];
      dp[j] = Math.min(
        dp[j] + 1, // deletion
        dp[j - 1] + 1, // insertion
        prev + (a[i - 1] === b[j - 1] ? 0 : 1) // substitution
      );
      prev = tmp;
    }
    // borne : si toute la ligne > 2, coupe court
    if (Math.min(...dp) > 2) return 3;
  }
  return dp[n];
}

/* Score d’un champ par rapport à un token (exact > prefix > substring > fuzzy) */
function scoreTokenField(field, token) {
  if (!field) return 0;
  const f = norm(field);
  if (!token) return 0;
  if (f === token) return 6;
  if (f.startsWith(token)) return 5;
  if (f.includes(token)) return 3;
  // fuzzy sur chaque mot du champ
  const words = f.split(" ").filter(Boolean);
  for (const w of words) {
    const d = levenshteinMax2(w, token);
    if (d === 1) return 2;
    if (d === 2) return 1;
  }
  return 0;
}

/* Score global d’un vin pour une liste de tokens, avec pondération par champ */
function scoreWineSmart(w, tokens) {
  if (!tokens.length) return 0;
  let s = 0;
  for (const t of tokens) {
    s +=
      5 * scoreTokenField(w.name, t) +
      3 * scoreTokenField(w.region, t) +
      2 * scoreTokenField(w.type, t) +
      2 * scoreTokenField(w.category, t) +
      1 * scoreTokenField(w.description, t) +
      2 * scoreTokenField(w.year ? String(w.year) : "", t);
  }
  // Bonus si tous les tokens apparaissent quelque part
  const hay = norm(
    [w.name, w.region, w.type, w.category, w.description, w.year].join(" ")
  );
  const allIn = tokens.every((t) => hay.includes(t));
  if (allIn) s += 5;
  return s;
}

/* Mise en évidence simple des termes trouvés (nom + région) */
function highlight(text, tokens) {
  if (!text || tokens.length === 0) return text;
  let out = text;
  const sorted = [...tokens].sort((a, b) => b.length - a.length);
  for (const tk of sorted) {
    if (!tk) continue;
    const re = new RegExp(
      `(${tk.replace(/[.*+?^${}()|[\\]\\\\]/g, "\\$&")})`,
      "ig"
    );
    out = out.replace(re, "<mark>$1</mark>");
  }
  return out;
}

/* ===== Page ===== */
export default function Search() {
  const [params, setParams] = useSearchParams();
  const q = params.get("q") || "";
  const [wines, setWines] = useState([]);
  const [loading, setLoading] = useState(true);

  // filtres optionnels (UI)
  const [cat, setCat] = useState(params.get("cat") || "");
  const [type, setType] = useState(params.get("type") || "");

  useEffect(() => {
    (async () => {
      try {
        const snap = await getDocs(collection(db, "winesek"));
        const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setWines(data);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Listes pour filtres
  const { categories, types } = useMemo(() => {
    const cset = new Set();
    const tset = new Set();
    wines.forEach((w) => {
      if (w.category) cset.add(String(w.category));
      if (w.type) tset.add(String(w.type));
    });
    return {
      categories: Array.from(cset).sort(),
      types: Array.from(tset).sort(),
    };
  }, [wines]);

  // Parsing avancé: "cat:xxx type:yyy" dans q
  const { query, parsedCat, parsedType } = useMemo(() => {
    let queryText = q;
    let c = "";
    let t = "";
    queryText = queryText.replace(/(^|\s)cat:([^\s]+)/gi, (_, __, val) => {
      c = val;
      return " ";
    });
    queryText = queryText.replace(/(^|\s)type:([^\s]+)/gi, (_, __, val) => {
      t = val;
      return " ";
    });
    return {
      query: queryText.trim(),
      parsedCat: c,
      parsedType: t,
    };
  }, [q]);

  // Valeurs effectives des filtres (URL > UI)
  const effCat = (parsedCat || cat || "").toLowerCase();
  const effType = (parsedType || type || "").toLowerCase();

  // Prépare tokens (avec tolérance nombre)
  const tokens = useMemo(() => {
    const toks = tokenize(query);
    // Si recherche type "2018 bordeaux", les deux deviennent tokens
    return toks;
  }, [query]);

  // Filtrage & tri intelligents
  const results = useMemo(() => {
    const filtered = wines.filter((w) => {
      const wCat = String(w.category || "").toLowerCase();
      const wType = String(w.type || "").toLowerCase();
      if (effCat && !wCat.includes(effCat)) return false;
      if (effType && !wType.includes(effType)) return false;
      if (!tokens.length) return true;

      // Au moins un token doit “matcher” quelque part (souple)
      const fields = [
        w.name,
        w.region,
        w.description,
        w.category,
        w.type,
        w.year ? String(w.year) : "",
      ];
      const hasAny = tokens.some((t) =>
        fields.some((f) => scoreTokenField(f, t) > 0)
      );
      return hasAny;
    });

    return filtered
      .map((w) => ({ w, s: scoreWineSmart(w, tokens) }))
      .sort((a, b) => b.s - a.s)
      .map((x) => x.w);
  }, [wines, tokens, effCat, effType]);

  const total = results.length;

  // Suggestions “tu voulais dire…” si 0 résultat
  const suggestions = useMemo(() => {
    if (total !== 0 || !tokens.length) return [];
    const joined = tokens.join(" ");
    const jn = norm(joined);
    // score sur nom uniquement pour proposer 3 alternatives
    const scored = wines.map((w) => {
      const nameN = norm(w.name || "");
      const d = levenshteinMax2(nameN, jn);
      return { w, d };
    });
    return scored.sort((a, b) => a.d - b.d).slice(0, 3).map((x) => x.w);
  }, [total, tokens, wines]);

  // MAJ URL quand filtres UI changent
  const updateParam = (key, val) => {
    const next = new URLSearchParams(params);
    if (val) next.set(key, val);
    else next.delete(key);
    setParams(next, { replace: true });
  };

  // Mise en évidence (nom + région)
  const hiTokens = useMemo(() => tokens.filter((t) => t.length >= 2), [tokens]);

  return (
    <div className="container py-4">
      <h2 className="mb-3">Recherche</h2>

      <div className="row g-2 align-items-end mb-3">
        <div className="col-12 col-md-6">
          <div className="form-floating">
            <input
              id="q"
              className="form-control"
              placeholder="Rechercher…"
              value={q}
              onChange={(e) => {
                const next = new URLSearchParams(params);
                const v = e.target.value;
                if (v) next.set("q", v);
                else next.delete("q");
                setParams(next, { replace: true });
              }}
            />
            <label htmlFor="q">Mots-clés (ex: "bordeaux 2018")</label>
          </div>
        </div>

        <div className="col-6 col-md-3">
          <div className="form-floating">
            <select
              id="cat"
              className="form-select"
              value={cat}
              onChange={(e) => {
                setCat(e.target.value);
                updateParam("cat", e.target.value);
              }}
            >
              <option value="">Toutes catégories</option>
              {categories.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
            <label htmlFor="cat">Catégorie</label>
          </div>
        </div>

        <div className="col-6 col-md-3">
          <div className="form-floating">
            <select
              id="type"
              className="form-select"
              value={type}
              onChange={(e) => {
                setType(e.target.value);
                updateParam("type", e.target.value);
              }}
            >
              <option value="">Tous types</option>
              {types.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
            <label htmlFor="type">Type</label>
          </div>
        </div>
      </div>

      {loading ? (
        <p>Chargement…</p>
      ) : total === 0 ? (
        <div className="alert alert-light border">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <strong>Aucun résultat</strong>
              <div className="small text-muted">
                Essaie des mots-clés plus généraux, corrige l’orthographe, ou
                change de filtre.
              </div>
              {suggestions.length > 0 && (
                <div className="mt-2">
                  <div className="small text-muted">Suggestions :</div>
                  <ul className="mb-0">
                    {suggestions.map((w) => (
                      <li key={w.id}>
                        <Link to={`/product/${w.id}`}>{w.name}</Link>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            <Link to="/" className="btn btn-outline-secondary btn-sm">
              Revenir aux vins
            </Link>
          </div>
        </div>
      ) : (
        <>
          <div className="mb-3 text-muted">
            {total} résultat{total > 1 ? "s" : ""} trouvé{total > 1 ? "s" : ""}.
          </div>

          <div className="row">
            {results.map((wine) => {
              const nameHtml = highlight(wine.name || "", hiTokens);
              const regionHtml = highlight(wine.region || "", hiTokens);
              return (
                <div key={wine.id} className="col-6 col-md-4 col-lg-3 mb-4">
                  {/* On réutilise WineCard mais on montre aussi le titre/region surlignés si besoin */}
                  <WineCard wine={wine} />
                  <div className="mt-1">
                    <div
                      className="fw-semibold"
                      dangerouslySetInnerHTML={{ __html: nameHtml }}
                    />
                    <div
                      className="small text-muted"
                      dangerouslySetInnerHTML={{ __html: regionHtml }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* <p className="small text-muted mt-3">
        Astuces : utilise <code>cat:rouge</code> ou <code>type:red</code> dans
        la barre (ex: <em>cat:wine bordeaux 2018</em>). La recherche tolère
        aussi quelques fautes.
      </p> */}
    </div>
  );
}
